import dataModel from '../models/DataModel';
import _ from 'lodash';
import userService from '../services/userService';
import directoriesService from '../services/directoriesService';
import DAO from './DAO.js';
import StoreItemProcessing from '../helpers/StoreFunctions/StoreItemProcessing';
import OfflineQueue from './queue/OfflineQueue.js';
import ReduxConstants, { RecordStates } from '../constants/ReduxConstants.js';

const user_root = {
  user: {
    additionalFields: ['profile_image'], role: {}, companies: {}, company_vendor_users: { company: {}, vendor_user: { additionalFields: ['profile_image'], role: {}, companies: {} } },
  },
};

// TODO: our DAOs should not be classes - there is no concept of state
// we can use them as stateless function files and not have to import/instantiate them on dataModel
export default class UsersDAO extends DAO {

  constructor(store) {
    super(store, 'user');
    this.OfflineQueue = OfflineQueue;

    this.unsetUser = this.unsetUser.bind(this);
    this.clearUser = this.clearUser.bind(this);
    this.getEditorUserMarkets = this.getEditorUserMarkets.bind(this);
    this.setPassword = this.setPassword.bind(this);
    this.sendForgotPassword = this.sendForgotPassword.bind(this);
    this.sendInvite = this.sendInvite.bind(this);
    this.handleActionCableAndGetRequest = this.handleActionCableAndGetRequest.bind(this);
    this.checkUsersCollections = this.checkUsersCollections.bind(this);
  }

  unsetUser(userId) {
    StoreItemProcessing.deleteItemInStore(this.itemType, userId);
  }

  clearUser() {
    StoreItemProcessing.clearItemsInStore(this.itemType);
  }

  clearState(options) {
    if (!options.toIgnore) {
      options.toIgnore = {};
    }
    if (!options.toIgnore.users) {
      options.toIgnore.users = [];
    }
    if (options.toIgnore.users) {
      options.toIgnore.users.push('currentUser');
      options.toIgnore.users.push('permissions');
      options.toIgnore.users.push('userRoles');
    }
    this.clearMyState(this.stateTree.new_users, options.toIgnore.users, this.unsetUser, this.clearUser, options);
  }

  mapToReturn(user, options) {
    let denormalizedUser = super.mapToReturn(user, options);

    //what if we don't have builds here?
    if (denormalizedUser.company_vendor_users && options && options.recordDescriptor && options.recordDescriptor.user && options.recordDescriptor.user.company_vendor_users) {
      let vendorCompanies = denormalizedUser.company_vendor_users.map(cvu => {
        return cvu.company;
      });
      denormalizedUser.vendor_companies = vendorCompanies;
    }
    denormalizedUser.name = denormalizedUser.first_name + ' ' + denormalizedUser.last_name;
    
    return denormalizedUser;
  }

  getEditorUserMarkets(userId) {
    return dataModel.usersDAO.getUser(userId, { recordDescriptor: { user: { companies: {}, role: {}, markets: {} } } }).then(user => {
      let marketIds = null;
      if (user) {
        if (user.role && user.role.name == 'editor' && user.companies &&
          user.companies.length > 0 && user.companies[0].id == localStorage.current_company_id) {
          marketIds = user.markets.map(market => { return market.id; });
        }
      }
      return marketIds;
    });
  }

  get currentUser() {
    return this.stateTree.new_users ? this.stateTree.new_users.currentUser : null;
  }

  set currentUser(currentUser) {
    this.store.dispatch({
      type: 'CURRENT_USER_SET',
      currentUser: currentUser,
    });
  }

  clearPermissions() {
    // Reset to an empty permission set (not null) so components reading
    // e.g. `permissions.isSuperUser` during the sign-out render don't throw.
    this.store.dispatch({
      type: 'PERMISSIONS_SET',
      permissions: {},
    });
  }

  set quickAssignments(quickAssignments) {
    this.store.dispatch({
      type: 'QUICK_ASSIGN_SET',
      quickAssignments: quickAssignments,
    });
  }

  get quickAssignments() {
    return this.stateTree.new_users ? this.stateTree.new_users.quickAssignments : null;
  }

  set userRoles(userRoles) {
    this.store.dispatch({
      type: 'USER_ROLES_SET',
      userRoles: userRoles,
    });
  }

  getUser(userId, options, prefetchCallback, fromActionCable) {
    if (!options) {
      options = { recordDescriptor: user_root };
    }
    else if (!options.recordDescriptor) {
      options.recordDescriptor = user_root;
    }
    else if (!options.recordDescriptor.role || !options.recordDescriptor.companies) {
      options.recordDescriptor.role = {};
      options.recordDescriptor.companies = {};
    }
    return this._getItem(userId, userService.getUser.bind(this, userId, null, options), options, this.mapToReturn, null, prefetchCallback, fromActionCable).then(user => {
      if (fromActionCable && this.currentUser.id == user.id) {
        let currentUser = this.currentUser;
        currentUser.settings = Object.assign({}, user.settings);
        this.currentUser = currentUser;
      }
      return user;
    }
    );
  }

  getAllUsers(){
    return userService.getUsers().then((response)=>{
      return response.data;
    });
  }

  handleResponse(user, response, dontQueue, options, errorMessage) {
    if (response.offline && dontQueue) {
      return response;
    }
    else if (response.offline) {
      return response;
    }
    else if (response.error) {
      dataModel.displayHTTPError(errorMessage, response.error, false);
      this.markError(user);
      this.throwItemError();
    }
    else if (response.data) {
      this.normalizeAndSyncToStore(response, options);
      const denormalized = this.mapToReturn(this.findItem(response.data.id), options);
      return denormalized;
    }
  }

  createUser(user, options) {
    return userService.createUser(user, null, options).then(response => {
      return this.handleResponse(user, response, false, options, 'Creating a user');
    });
  }

  updateUser(user, options) {
   
    if (!options) {
      options = { recordDescriptor: user_root };
    }
    else if (!options.recordDescriptor) {
      options.recordDescriptor = user_root;
    }
    else if (!options.recordDescriptor.role || !options.recordDescriptor.companies) {
      options.recordDescriptor.role = {};
      options.recordDescriptor.companies = {};
    }
    const normalizedUser = this.normalizeItem(user, { store: true });
    this.markPending(normalizedUser);
    dataModel.actionCableConnector.internalUpdate({ type: 'updateUser', user_id: normalizedUser.id }, normalizedUser.id);
    if (this.currentUser && this.currentUser.id == normalizedUser.id) {
      this.currentUser = _.cloneDeep(user);
    }
    return userService.updateUser(user, null, options).then(response => {
      return this.handleResponse(user, response, false, options, 'Updating a user');
    });
  }

  deleteUser(user) {
    this.markDeleted(user);
    return userService.deleteUser(user.id).then(response => {
      return response;
    });
  }

  createVendorUser(vendorUser, options) {
    return userService.createVendorUser(vendorUser).then(response => {
      return this.handleResponse(vendorUser, response, false, options, 'Creating a vendor user');
    });
  }

  createVendorUsers(vendorUsers, options) {
    const vendorUserPromises =[];
    for(let vendorUser of vendorUsers) {
      vendorUserPromises.push(userService.createVendorUser(vendorUser, null, null, options));
    }
    return Promise.all(vendorUserPromises).then(results => {
      return results;
    });
  }

  createAdminUser(adminUser, options) {
    return userService.createAdminUser(adminUser, null, null, options).then(response => {
      return this.handleResponse(adminUser, response, false, options, 'Creating an admin user');
    });
  }

  deleteVendorUser(vendorUser) {
    userService.deleteVendorUser(vendorUser).then(response => {
      if (response.error) {
        dataModel.displayHTTPError('Deleting a vendor user', response.error);
      }
      else {
        dataModel.catastrophicError = null;
        dataModel.displayInfoErrorMessage('Vendor user removed succesfully', 4000, 1);
      }
    });    
  }

  deleteVendorUsers(vendorUsers) {
    const vendorUserPromises =[];
    for(let vendorUser of vendorUsers) {
      vendorUserPromises.push(userService.deleteVendorUser(vendorUser));
    }
    return Promise.all(vendorUserPromises).then(results => {
      return results;
    });
  }

  sendInvite(user) {
    userService.sendInvite(user).then(response => {
      if (response.error) {
        dataModel.displayHTTPError('Sending email', response.error);
      }
      else {
        dataModel.catastrophicError = null;
        dataModel.displayInfoErrorMessage('Invitation sent', 4000, 1);
      }
    });
  }

  sendForgotPassword(email){
    userService.sendResetPasswordEmail(email).then(response => {
      if(!response.error){
        dataModel.displayInfoErrorMessage('Email sent', 4000, 1);        
      }
      else{
        dataModel.displayInfoErrorMessage(`Send Forgot Password Email: ${response.error.message}`, 4000, 2);
      }
    });
  }

  setPassword(password, token) {
    return userService.setPassword(password, token);
  }

  getUserRoles() {
    return userService.getUserRoles().then(userRoles => {
      if (userRoles.error) {
        dataModel.displayHTTPError('Getting user roles', userRoles.error);
        return null;
      }
      else {
        return userRoles;
      }
    });
  }

  getAdminUserRoles() {
    return dataModel.companiesDAO.getAdminCompany().then(adminCompany => {
      if (adminCompany) {
        return userService.getUserRoles(adminCompany.id).then(userRoles => {
          if (userRoles.error) {
            dataModel.displayHTTPError('Getting user roles', userRoles.error);
            return null;
          }
          else {
            return userRoles;
          }
        });
      }
    });
  }

  getVendors(page, perPage, sortfield, ascending, options) {
    if (this.stateTree.new_companies.currentCompany) {
      // dataModel.inProgress = 25000
      userService.getVendorUsers(options).then(response => {
        // dataModel.inProgress = 0
        if (response.error) {
          dataModel.displayHTTPError('Getting users', response.error, true);
          return null;
        }
        else {
          if (options.clear) {
          }
          
        }
      });
    }
    else {
      return null;
    }
  }

  initializePermissions(user, newCompany) {

    if (!user) {
      return;
    }
    const company = newCompany ? newCompany : this.stateTree.new_companies ? this.stateTree.new_companies.currentCompany : null;
    const isVendor = company ? company.id != user.companies[0].id : false;
    const userPermissions = {};
    if (user.role) {
      const userRole = user.role;
      user.role_name = userRole.name;
      if (user.super_admin) {
        userPermissions.isSuperUser = true;
        userPermissions.canCreateCompany = true;
        userPermissions.canDeleteCompany = true;
        userPermissions.canEditCompany = true;
        userPermissions.canViewCompany = true;
        userPermissions.canCreateDirectory = true;
        userPermissions.canDeleteDirectory = true;
        userPermissions.canEditDirectory = true;
        userPermissions.canViewDirectory = true;
        userPermissions.canCreateUser = true;
        userPermissions.canDeleteUser = true;
        userPermissions.canEditUser = true;
        userPermissions.canViewUser = true;
        userPermissions.canCreateLocation = true;
        userPermissions.canDeleteLocation = true;
        userPermissions.canEditLocation = true;
        userPermissions.canViewPOR = true;
        userPermissions.canCreateProject = true;
        userPermissions.canDeleteProject = true;
        userPermissions.canEditProject = true;
        userPermissions.canAddProjectMembers = true;
        userPermissions.canViewProject = true;
        userPermissions.canCopyProject = true;
        userPermissions.canViewProject = true;
        userPermissions.canEditBuild = true;
        userPermissions.canCreateTask = true;
        userPermissions.canDeleteTask = true;
        userPermissions.canEditTask = true;
        userPermissions.canViewTask = true;
        userPermissions.canCompleteTask = true;
        userPermissions.canCreateProjectTemplate = true;
        userPermissions.canDeleteProjectTemplate = true;
        userPermissions.canEditProjectTemplate = true;
        userPermissions.canViewProjectTemplate = true;
        userPermissions.canCreateProjectType = true;
        userPermissions.canDeleteProjectType = true;
        userPermissions.canEditProjectType = true;
        userPermissions.canViewProjectType = true;
        userPermissions.canCreateLocation = true;
        userPermissions.canEditLocation = true;
        userPermissions.canDeleteLocation = true;
        userPermissions.canCreateReferenceDocument = true;
        userPermissions.canDeleteReferenceDocument = true;
        userPermissions.canViewReferenceDocument = true;
        userPermissions.canCreateMarkets = true;
        userPermissions.canEditMarkets = true;
        userPermissions.canDeleteMarkets = true;
        userPermissions.canCreateLocationUsers = true;
        userPermissions.canEditLocationUsers = true;
        userPermissions.canDeleteLocationUsers = true;
      }
      else {
        switch (userRole.name) {
        case 'primary_contact':
          userPermissions.canViewDirectory = true;
          userPermissions.canViewUser = true;
          userPermissions.canViewTask = true;
          userPermissions.canCompleteTask = true;
          userPermissions.canViewPOR = true;
          break;
        case 'editor':
          userPermissions.canViewCompany = false;
          userPermissions.canViewUser = true;
          userPermissions.canViewDirectory = true;
          userPermissions.canCreateProject = false;
          userPermissions.canAddProjectMembers = !isVendor;
          userPermissions.canViewProjectType = !isVendor;
          userPermissions.canViewProject = true;
          userPermissions.canEditProject = false;
          userPermissions.canEditBuild = !isVendor;
          userPermissions.canCreateBuild = !isVendor;
          userPermissions.canDeleteBuild = !isVendor;
          userPermissions.canCreateTask = !isVendor;
          userPermissions.canEditTask = !isVendor;
          userPermissions.canDeleteTask = !isVendor;
          userPermissions.canViewTask = true;
          userPermissions.canCompleteTask = true;
          userPermissions.canCreateLocation = !isVendor;
          userPermissions.canDeleteLocation = !isVendor;
          userPermissions.canEditLocation = !isVendor;
          userPermissions.canViewPOR = true;
          userPermissions.canCreateReferenceDocument = !isVendor;
          userPermissions.canDeleteReferenceDocument = !isVendor;
          userPermissions.canViewReferenceDocument = !isVendor;
          break;
        case 'standard_user':
          userPermissions.canViewDirectory = true;
          userPermissions.canViewUser = true;
          userPermissions.canViewTask = true;
          userPermissions.canCompleteTask = true;
          userPermissions.canViewPOR = true;
          break;
        case 'view_only':
          userPermissions.canViewDirectory = true;
          userPermissions.canViewUser = true;
          userPermissions.canViewProject = true;
          userPermissions.canViewTask = true;
          userPermissions.canViewPOR = true;
          break;
        case 'insights':
        case 'Insights':
          userPermissions.isInsightsOnlyUser = true;
          break;
        case 'admin':
          userPermissions.canCreateCompany = !isVendor;
          userPermissions.canEditCompany = !isVendor;
          userPermissions.canViewCompany = true;
          userPermissions.canCreateDirectory = !isVendor;
          userPermissions.canEditDirectory = !isVendor;
          userPermissions.canDeleteDirectory = !isVendor;
          userPermissions.canViewDirectory = true;
          userPermissions.canCreateUser = !isVendor;
          userPermissions.canEditUser = !isVendor;
          userPermissions.canDeleteUser = !isVendor;
          userPermissions.canViewUser = true;
          userPermissions.canViewProjectType = !isVendor;
          userPermissions.canCreateProject = !isVendor;
          userPermissions.canDeleteProject = !isVendor;
          userPermissions.canEditProject = !isVendor;
          userPermissions.canAddProjectMembers = !isVendor;
          userPermissions.canCopyProject = !isVendor;
          userPermissions.canViewProject = true;
          userPermissions.canEditBuild = !isVendor;
          userPermissions.canCreateTask = !isVendor;
          userPermissions.canDeleteTask = !isVendor;
          userPermissions.canEditTask = !isVendor;
          userPermissions.canViewTask = true;
          userPermissions.canCompleteTask = true;
          userPermissions.canViewProjectReport = !isVendor;
          userPermissions.canViewUserReport = !isVendor;
          userPermissions.canViewProjectTemplate = !isVendor;
          userPermissions.canCreateLocation = !isVendor;
          userPermissions.canEditLocation = !isVendor;
          userPermissions.canDeleteLocation = !isVendor;
          userPermissions.canCreateProjectTemplate = !isVendor;
          userPermissions.canDeleteProjectTemplate = true;
          userPermissions.canEditProjectTemplate = !isVendor;
          userPermissions.canViewProjectTemplate = !isVendor;
          userPermissions.canCreateProjectType = !isVendor;
          userPermissions.canEditProjectType = !isVendor;
          userPermissions.canViewProjectType = !isVendor;
          userPermissions.canCreateLocation = !isVendor;
          userPermissions.canDeleteLocation = !isVendor;
          userPermissions.canEditLocation = !isVendor;
          userPermissions.canViewPOR = true;
          userPermissions.canCreateReferenceDocument = !isVendor;
          userPermissions.canDeleteReferenceDocument = !isVendor;
          userPermissions.canViewReferenceDocument = !isVendor;
          userPermissions.canCreateProjectTemplate = !isVendor;
          userPermissions.canEditProjectTemplate = !isVendor;
          userPermissions.canViewProjectTemplate = !isVendor;
          userPermissions.canCreateProjectType = !isVendor;
          userPermissions.canEditProjectType = !isVendor;
          userPermissions.canViewProjectType = !isVendor;
          userPermissions.canCreateMarkets = !isVendor;
          userPermissions.canEditMarkets = !isVendor;
          userPermissions.canDeleteMarkets = !isVendor;
          userPermissions.canCreateLocationUsers = !isVendor;
          userPermissions.canEditLocationUsers = !isVendor;
          userPermissions.canDeleteLocationUsers = !isVendor;
          break;
        default:
          break;
        }
      }
    }
    else {
      userPermissions.canViewDirectory = true;
      userPermissions.canViewUser = true;
      userPermissions.canViewProject = true;
      userPermissions.canViewTask = true;
    }
    this.store.dispatch({
      type: 'PERMISSIONS_SET',
      permissions: userPermissions,
    });
  }

  getProjectManagers(market, options) {
    return new Promise((resolve) => {
      if (market) {
        if (Array.isArray(market)) {
          let projectManagers = [];
          market.forEach(async m => {
            let fetchedMarket = await dataModel.marketsDAO.getMarket(m.id, null, {
              recordDescriptor: {
                market:
                {
                  market_users:
                    { user: { role: {}, additionalFields: ['profile_image'] } },
                  market_vendor_users:
                  {
                    vendor_user: { role: {}, additionalFields: ['profile_image'], vendor_company: {} },
                  },
                },
              },
            });
            if (fetchedMarket) {
              if (fetchedMarket.market_users) {
                fetchedMarket.market_users.forEach(mu => {
                  if (mu.user && !mu.user.suspended && mu.user.role && (mu.user.role.name == 'editor' || mu.user.role.name == 'admin')) {
                    if (projectManagers.length == 0 || !projectManagers.find(pm => {
                      return pm.id == mu.user.id;
                    })) {
                      if (!mu.user.name) {
                        mu.user.name = mu.user.first_name + ' ' + mu.user.last_name;
                      }
                      projectManagers.push(mu.user);
                    }
                  }
                });
              }
            }
          });
          return resolve(projectManagers);
        }
        else {
          return resolve(dataModel.marketsDAO.getMarket(market.id, null, {
            recordDescriptor: {
              market:
              {
                market_users:
                  { user: { role: {}, additionalFields: ['profile_image'] } },
                market_vendor_users:
                {
                  vendor_user: { role: {}, additionalFields: ['profile_image'], vendor_company: {} },
                },
              },
            },
          }).then(market => {
            let projectManagers = [];
            if (market) {
              if (market.market_users) {
                market.market_users.forEach(mu => {
                  if (mu.user && !mu.user.suspended && mu.user.role && (mu.user.role.name == 'editor' || mu.user.role.name == 'admin')) {
                    projectManagers.push(mu.user);
                  }
                });
              }
            }
            return projectManagers;
          })
          );
        }
      }
      else {
        return resolve(directoriesService.getProjectManagers(options).then(response => {
          if (!response.error) {
            const normalizedUsers = this.normalizeAndSyncCollectionToStore(response, options);
            return this.denormalizeCollection(normalizedUsers, options ? options.recordDescriptor : null);
          }
        })
        );
      }
    });
  }

  checkUsersCollections(user_id) {
    let collectionIds = dataModel.collectionsDAO.getCollectionsByType(ReduxConstants.USER);
    for (let collectionId of collectionIds) {
      let collection = dataModel.collectionsDAO.findCollection(collectionId);  

      if (collection && !collection.ids.find(id => {return id == user_id;})){
        collection._recordState = RecordStates.NEEDS_SYNC;
        dataModel.collectionsDAO.setCollection(collectionId, collection, ReduxConstants.USER);
      }
    }
  }

  handleActionCableAndGetRequest(acData) {    
    let user = this.findItem(acData.user_id);
    if (user) {
      if (acData.is_attribute_update) {
        this.markNeedsAttributeSync(user);
      }
      else {
        this.markNeedsSync(user);
      }
    }
    this.checkUsersCollections(acData.user_id);
    switch (acData.type) {
    case 'newUser':
    case 'updateUser':
    case 'newVendorUser':
      return this.getUser.bind(this);
    case 'deleteUser':
      this.unsetUser(acData.user_id);
      return user;
    default:
    }

  }
}



// WEBPACK FOOTER //
// ./src/dao/UsersDAO.js